import * as React from 'react';

import { Button } from '../button/button.tsx';
import { Icon } from '../icon/icon.tsx';

import { useDevice, useWindowSize } from '../hooks.tsx';

import './alert-message.css';
import clsx from 'clsx';

interface AlertMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: 'color' | 'neutral' | 'warning' | 'danger' | 'field';
  title?: string;
  description?: React.ReactNode;
  icon?: 'top' | 'left' | 'right';
  button?: {
    position: 'bottom' | 'right';
    label: string;
    cta: () => void;
  };
  style?: React.CSSProperties;
}

const AlertMessage = React.forwardRef<HTMLDivElement, AlertMessageProps>(
  ({ variant, title, description, icon, button, className, style, ...props }, ref) => {
    const dimensions = useWindowSize();
    const device = useDevice(dimensions);

    const iconColorRegistry = {
      color: 'var(--color_primary)',
      neutral: 'var(--color_neutral)',
      warning: 'var(--color_warning-dark)',
      danger: 'var(--color_error)',
      field: 'var(--color_system-background)',
    };

    const containerClasses = clsx(
      'alert-message',
      {
        [`alert-message-${variant}`]: variant,
      },
      className,
    );

    return (
      <div
        className={containerClasses}
        ref={ref}
        {...props}
        style={{
          display: 'flex',
          gap: icon !== 'top' ? '20px' : '5px',
          flexDirection:
            icon === 'left'
              ? 'row-reverse'
              : icon === 'top'
                ? 'column-reverse'
                : 'row',
          alignItems: icon !== 'top' ? 'center' : 'flex-start',
          ...style,
        }}
      >
        <div
          className="alert-message-container"
          style={{
            flexDirection:
              button?.position === 'bottom' || device === 'mobile'
                ? 'column'
                : 'row',
            alignItems:
              button?.position === 'bottom' || device === 'mobile'
                ? 'flex-start'
                : 'center',
          }}
        >
          <div
            className="alert-message-container-content"
            style={{
              flexDirection:
                button && button.position !== 'right' && variant !== 'field'
                  ? 'column-reverse'
                  : 'column',
              marginBottom:
                button?.position === 'bottom' || device === 'mobile'
                  ? '20px'
                  : '0',
              marginRight: button?.position === 'right' ? '20px' : '0',
            }}
          >
            {title && (
              <div
                className="alert-message-container-content-title"
                style={{
                  color:
                    variant === 'color'
                      ? 'var(--color_primary-darkest)'
                      : 'inherit',
                }}
              >
                {title}
              </div>
            )}
            {description !== undefined && (
              <div className="alert-message-container-content-description">
                {description}
              </div>
            )}
          </div>
          {button && (
            <div className="alert-message-container-button">
              <Button asToast onClick={button.cta}>
                {button?.label}
              </Button>
            </div>
          )}
        </div>
        {icon && variant !== 'field' && (
          <div className="alert-message-icon">
            <Icon
              type="warning"
              size="large"
              color={iconColorRegistry[variant]}
            />
          </div>
        )}
      </div>
    );
  },
);

export { AlertMessage };
